import { Types as CoreTypes, utilities } from '@cornerstonejs/core';
import { Types as ToolTypes, annotation } from '@cornerstonejs/tools';
import { Internal, OHIF, Api } from '@gleamer/types';
import { DicomMetadataStore } from '@ohif/core';
import GleamerPredictionRectangleTool from './GleamerPredictionRectangleTool';
import GleamerPredictionContourTool from './GleamerPredictionContourTool';
import tinycolor from 'tinycolor2';
import { is2DPrediction, isContourDetection } from '../utils/predictions.utils';

type Annotation = ToolTypes.Annotation;
type IStackViewport = CoreTypes.IStackViewport;
type PredictionInternal = Internal.PredictionInternal;
type DetectionCleaningStatus = Api.DetectionCleaningStatus;

function isMatchingInstance(
  instance: { SeriesInstanceUID: string; StudyInstanceUID: string } | undefined,
  prediction: PredictionInternal
) {
  return (
    instance &&
    instance.SeriesInstanceUID === prediction.seriesInstanceUID &&
    instance.StudyInstanceUID === prediction.studyInstanceUID
  );
}

function mapDetectionStatusToIcon(status: DetectionCleaningStatus) {
  switch (status) {
    case 'POSITIVE':
      return '✅';
    case 'NEGATIVE':
      return '❌';
    case 'UNCERTAIN':
      return '❓';
    default:
      return '';
  }
}

export function drawPredictions(
  predictions: PredictionInternal[],
  viewport: IStackViewport,
  annotationToHighlight: string,
  boxesColor?: string
): Annotation[] {
  const annotationManager = annotation.state.getAnnotationManager();

  if (!predictions) {
    return [];
  }

  const generatedAnnotations = predictions
    .flatMap((pred) => {
      return pred.detections.map((detection) => {
        let instance: any;
        // The stack image the detection is drawn on. On a multi-frame DBT the
        // instance's imageId is not a frame's, so a contour, which only shows on
        // its own slice, needs the frame's.
        let imageId: string;
        if (is2DPrediction(pred)) {
          instance = DicomMetadataStore.getInstance(
            pred.studyInstanceUID,
            pred.seriesInstanceUID,
            pred.sopInstanceUID
          );
        } else {
          const { x, y, z } = detection.points[0];
          const imageIndex = utilities.getClosestStackImageIndexForPoint(
            [x, y, z],
            viewport
          );

          imageId = viewport.getImageIds().at(imageIndex);
          instance = DicomMetadataStore.getInstanceByImageId(imageId);
        }

        if (!isMatchingInstance(instance, pred)) {
          return null;
        }

        const annotationUID = detection.annotationId;
        const color = boxesColor || `rgb(0, 255, 0)`;
        const annotationStyle = annotation.config.style;
        const { lineWidth } = annotationStyle.getDefaultToolStyles().global;

        const colorHovered = tinycolor(color).lighten(20).toRgbString();

        const lineWidthHovered = `${parseFloat(lineWidth) * 3}`;
        annotationStyle.setAnnotationStyles(annotationUID, {
          color: color,
          colorActive: color,
          colorEnabled: color,
          // Contours are locked; keep them the prediction colour.
          colorLocked: color,
          colorHighlighted: colorHovered,
          colorSelected: colorHovered,
          lineWidthSelected: lineWidthHovered,
          lineWidthHighlighted: lineWidthHovered,
        });

        let label = `${detection.type} - ${detection.confidence.toFixed(1)}`;
        if (detection.status) {
          label = `${label} - ${mapDetectionStatusToIcon(detection.status)}`;
        }

        const isContour = isContourDetection(detection);

        const predictionAnnotation: Annotation = {
          annotationUID,
          highlighted: annotationToHighlight === detection.annotationId,
          // A contour (Pre-annotation) is never moved or edited (locked below).
          isLocked: isContour,
          invalidated: false,
          metadata: {
            ...viewport.getViewReference(),
            toolName: isContour
              ? GleamerPredictionContourTool.toolName
              : GleamerPredictionRectangleTool.toolName,
            referencedImageId:
              isContour && imageId ? imageId : instance.imageId,
            FrameOfReferenceUID: instance.FrameOfReferenceUID,
          },
          data: {
            label,
            handles: {
              points: detection.points.map((point) => {
                if (is2DPrediction(pred)) {
                  return utilities.imageToWorldCoords(instance.imageId, [
                    point.x,
                    point.y,
                  ]);
                } else {
                  return [point.x, point.y, point.z];
                }
              }),
            },
          },
        };

        return predictionAnnotation;
      });
    })
    .filter(Boolean) as Annotation[];

  generatedAnnotations.forEach((predictionAnnotation) => {
    if (!annotationManager.getAnnotation(predictionAnnotation.annotationUID)) {
      annotationManager.addAnnotation(predictionAnnotation);
      // Cornerstone keeps the lock state in its own set and resets `isLocked`
      // from it on add, so a contour is locked through the locking API.
      if (
        predictionAnnotation.metadata.toolName ===
        GleamerPredictionContourTool.toolName
      ) {
        annotation.locking.setAnnotationLocked(
          predictionAnnotation.annotationUID,
          true
        );
      }
    }
  });

  return generatedAnnotations;
}
